import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import {
  FiHome,
  FiCalendar,
  FiUsers,
  FiSettings,
  FiTag,
  FiBookOpen,
  FiUser,
  FiBarChart2,
} from "react-icons/fi";
import { MdOutlineInventory } from "react-icons/md";
import { BsEmojiSmile } from "react-icons/bs";
import { TbLayoutGridAdd, TbAutomaticGearbox } from "react-icons/tb";
import {
  MdLogout,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
} from "react-icons/md";
import useAuthStore from "../../store/authStore";
import userManagementStore from "../../store/userManagementStore";
import { canAccessPath, isManager, roleLabel } from "../../utils/permissions";

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeParent, setActiveParent] = useState(null);
  const [showSubSideBar, setShowSubSideBar] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);

  const { user, logout } = useAuthStore();
  const { roles, getAllRole } = userManagementStore();

  const userIsManager = isManager(user);

  useEffect(() => {
    // La liste des rôles n'est accessible qu'aux gestionnaires (API)
    if (userIsManager) getAllRole();
  }, [getAllRole, userIsManager]);

  // Fonction pour gérer la responsivité
  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const mobile = screenWidth < 1024;

      setIsMobileView(mobile);

      if (mobile) {
        setShowSubSideBar(false);
      } else {
        setShowSubSideBar(true);
      }
    };

    // Appeler une fois au montage pour définir l'état initial
    handleResize();

    // Ajouter l'écouteur d'événement
    window.addEventListener("resize", handleResize);

    // Nettoyer l'écouteur d'événement
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Fonction pour gérer le clic sur un lien du subsidebar
  const handleSubSidebarClick = () => {
    if (isMobileView) {
      setShowSubSideBar(false);
    }
  };

  // Libellé français du rôle (Employé, Financier...)
  const formatRoleName = (roleName) => roleLabel(roleName);

  const links = useMemo(() => {
    const allLinks = [
      { label: "Accueil", to: "/index", icon: <FiHome size={22} /> },
      {
        label: "Dashboard",
        to: "/admin/dashboard",
        icon: <TbLayoutGridAdd size={22} />,
      },
      {
        label: "Calendrier/programmation",
        to: "/admin/workers",
        icon: <FiCalendar size={22} />,
      },
      {
        label: "Ventes",
        icon: <FiTag size={22} />,
        children: [
          { label: "Résumé des ventes quotidiennes", to: "/admin/sales-day" },
          { label: "Rendez-vous", to: "/admin/rendez-vous" },
          { label: "Ventes", to: "/admin/sales" },
          { label: "Paiements", to: "/admin/paiements" },
          { label: "Cartes cadeaux", to: "/admin/gift-cards" },
          { label: "Abonnements vendus", to: "/admin/sales-suscribtions" },
        ],
      },
      {
        label: "Équipe",
        icon: <FiUsers size={22} />,
        children: [
          { label: "Toute l'équipe", to: "/admin/teams" },
          // Ajout dynamique des rôles
          ...(roles
            ?.map((role) => {
              if (["superadmin", "client"].includes(role.name)) return null;
              return {
                label: formatRoleName(role.name),
                to: `/admin/team/${role.id}`,
              };
            })
            .filter(Boolean) || []),
        ],
      },
      {
        label: "Clients",
        to: "/admin/custumers",
        icon: <BsEmojiSmile size={22} />,
      },
      {
        label: "Catalogue",
        icon: <FiBookOpen size={22} />,
        children: [
          { label: "Catalogue de prestations", to: "/admin/catalogue" },
          { label: "Abonnements", to: "/admin/subscriptions" },
          // { label: "Produits", to: "/admin/catalogue-products" },
          // { label: "Inventaires", to: "/admin/inventaires" },
          // { label: "Commandes de stock", to: "/admin/stock-commande" },
          // { label: "Cartes cadeaux", to: "/admin/gift-cards" },
          // { label: "Fournisseurs", to: "/admin/fournisseurs" },
        ],
      },
      {
        label: "Inventaires",
        icon: <MdOutlineInventory size={22} />,
        children: [
          // { label: "Catalogue de prestations", to: "/admin/catalogue" },
          // { label: "Abonnements", to: "/admin/subscriptions" },
          { label: "Produits", to: "/admin/products" },
          { label: "Inventaires", to: "/admin/inventaires" },
          // { label: "Commandes de stock", to: "/admin/stock-commande" },
          // { label: "Cartes cadeaux", to: "/admin/gift-cards" },
          // { label: "Fournisseurs", to: "/admin/fournisseurs" },
        ],
      },
      {
        label: "Rapports",
        to: "/admin/rapport",
        icon: <FiBarChart2 size={22} />,
      },
      // {
      //   label: "Statistiques",
      //   to: "/admin/statistiques",
      //   icon: <FaChartLine size={22} />,
      // },
      {
        label: "Automatisations",
        to: "/admin/automatisations",
        icon: <TbAutomaticGearbox size={22} />,
        // children: [
        //   { label: "Fonctionnalités", to: "/admin/fonctionnalite" },
        //   { label: "Automatisations", to: "/admin/automatisations" },
        // ],
      },
      {
        label: "Paramètres",
        icon: <FiSettings size={22} />,
        children: [
          { label: "Configuration du salon", to: "/admin/salon" },
          { label: "Journal d'activité", to: "/admin/journal" },
        ],
      },
    ];

    // Masquer les pages non autorisées pour le rôle de l'utilisateur
    return allLinks
      .map((link) => {
        if (!link.children) {
          return canAccessPath(user, link.to) ? link : null;
        }
        const children = link.children.filter((child) =>
          canAccessPath(user, child.to),
        );
        return children.length > 0 ? { ...link, children } : null;
      })
      .filter(Boolean);
  }, [roles, user]);

  useEffect(() => {
    const match = links.find((link) => {
      if (link.children) {
        return link.children.some((child) =>
          location.pathname.startsWith(child.to),
        );
      }
      return location.pathname.startsWith(link.to || "");
    });

    if (match?.label) setActiveParent(match.label);
  }, [location.pathname, roles, links]);

  const handleLogin = () => {
    const currentPath = location.pathname.startsWith("/")
      ? location.pathname.slice(1)
      : location.pathname;
    const encodedPath = encodeURIComponent(currentPath);

    console.log({ currentPath, encodedPath });

    setTimeout(() => {
      navigate(`/login/${encodedPath}`);
    }, 1000);
  };

  const parent = links.find((l) => l.label === activeParent);

  return (
    <div className="flex h-screen">
      {/* Sidebar principale */}
      <aside className="px-2 md:w-20 bg-black text-white flex flex-col items-center py-4 gap-4 relative">
        {links.map(({ to, label, icon, children }) => {
          const isActive = location.pathname.startsWith(to);

          return !children ? (
            <button
              key={label}
              onClick={() => navigate(to)}
              className={`group relative flex items-center justify-center w-10 h-10 transition ${
                isActive ? "bg-secondary-700" : "hover:bg-secondary-200"
              }`}
            >
              {icon}
              <div className="absolute left-[120%] z-10 whitespace-nowrap bg-white px-3 py-1 shadow-md scale-0 group-hover:scale-100 transition font-nunito text-[14px] text-text">
                {label}
              </div>
            </button>
          ) : (
            <button
              key={label}
              onClick={() => {
                setActiveParent(label);
                setShowSubSideBar(true);
              }}
              className={`group relative flex items-center justify-center w-10 h-10 transition ${
                activeParent === label
                  ? "bg-secondary-700"
                  : "hover:bg-secondary-200"
              }`}
            >
              {icon}
              <div className="absolute left-[120%] z-10 whitespace-nowrap bg-white px-3 py-1 shadow-md scale-0 group-hover:scale-100 transition font-nunito text-[14px] text-text">
                {label}
              </div>
            </button>
          );
        })}

        {/* Icône profil en bas */}
        <div className="mt-auto">
          <button
            onClick={() => {
              if (user?.prenom && user?.nom) {
                if (
                  window.confirm(
                    `Se déconnecter de ${user?.prenom || ""} ${
                      user?.nom || ""
                    } ?`,
                  )
                ) {
                  logout();
                  handleLogin();
                }
              } else {
                handleLogin();
              }
            }}
            className={`group relative flex items-center justify-center w-10 h-10 transition text-[red] hover:text-white ${
              user?.prenom && user?.nom
                ? `hover:bg-[red]`
                : "hover:bg-secondary-500"
            }`}
            title={
              user?.prenom && user?.nom ? "Se déconnecter" : "Se connecter"
            }
          >
            {user?.prenom && user?.nom ? (
              <MdLogout size={22} />
            ) : (
              <FiUser size={22} />
            )}
            <div className="absolute left-[120%] z-10 whitespace-nowrap bg-white px-3 py-1 shadow-md scale-0 group-hover:scale-100 transition font-nunito text-[14px] text-text">
              {`${
                user?.prenom && user?.nom
                  ? `${user?.prenom || ""} ${user?.nom || ""}`
                  : "Se connecter"
              }`}
            </div>
          </button>
        </div>
      </aside>

      {/* Sous-menu latéral */}
      {parent?.children && (
        <aside
          className={`relative bg-white text-black border-r shadow-md flex flex-col p-4 ${
            showSubSideBar ? "w-64 " : "w-1"
          }`}
        >
          <button
            onClick={() => {
              setShowSubSideBar(!showSubSideBar);
            }}
            className="absolute bottom-5 md:top-5 -right-5 bg-white border flex items-center justify-center w-10 h-10 transition hover:bg-secondary-200"
            title={showSubSideBar ? "Masquer le menu" : "Afficher le menu"}
          >
            {showSubSideBar ? (
              <MdOutlineChevronLeft />
            ) : (
              <MdOutlineChevronRight />
            )}
          </button>
          <div className={`${showSubSideBar ? "visible" : "hidden"}`}>
            <h3 className="font-poppins font-semibold text-[18px] text-title mb-4 px-4">
              {activeParent}
            </h3>
            <nav className="flex flex-col gap-2">
              {parent.children.map((child) => (
                <NavLink
                  key={child.to}
                  to={child.to}
                  onClick={handleSubSidebarClick}
                  className={({ isActive }) =>
                    `px-4 py-2 font-nunito text-[14px] text-text ${
                      isActive ? "bg-secondary-100" : "hover:bg-gray-100"
                    }`
                  }
                >
                  {child.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </aside>
      )}
    </div>
  );
}
